/* DNOVA spec search (issue #39). Research and measurements: storefront/SEARCH-RESEARCH.md.
 *
 * A query is read as FAMILY + SPECS + text, and every recognised part becomes a Search & Discovery filter
 * on /search instead of a word: Shopify matches typed specs loosely ("24v" as text finds 181 solenoid
 * valves, the 24V filter 105) and pads every query semantically, while its filters act exactly on the
 * whole match set. So:
 *
 *   "1 inch brass solenoid 24V DC normally closed"
 *     → chips  [Solenoid valves] [Size DN25 · 1″] [Voltage 24 V] [Body Brass]   text "normally closed"
 *     → /search?q=normally+closed&filter.p.product_type=Solenoid+valve&filter.v.option.connection+size=…
 *
 * - The field on the search page holds the chips; a recognised spec turns into a chip as soon as it is
 *   typed (space), Backspace in an empty field removes the last one, × removes one and reloads. Chips are
 *   rebuilt from the URL, so they and the sidebar (Trade's facets, ticked) never disagree.
 * - A family narrower than its product type, and a drive on a valve, go into q in Shopify's field syntax
 *   (`title:knife`); any syntax in q also switches semantic padding off for that query.
 * - Every other search field (the header's) is turned into the same URL on submit; a raw /search?q= that
 *   still holds specs is converted on load unless the buyer asked for text (dn_raw=1).
 * - The dropdown (Trade's predictive panel) leads with what Enter will do and its exact count, then the
 *   categories that hold results; products show only for model codes and SKUs (exact-match lookups).
 * - Honest about the data: the voltage filter has no AC/DC, so "24V DC" applies 24 V and says so.
 *
 * Vocabulary: assets/dnova-search-vocab.json (tools/storefront/build-search-vocab.mjs). Counts:
 * /search?…&view=dnfacets (templates/search.dnfacets.liquid). DnovaSpec.plan('…') in the console.
 */
(() => {
  const script = document.currentScript;
  const VOCAB_URL = script && script.dataset.vocab;
  if (!VOCAB_URL || window.DnovaSpec) return;
  let vocabPromise;
  const vocab = () => (vocabPromise ||= fetch(VOCAB_URL).then((r) => r.json()).catch(() => null));

  const P = { size: 'filter.v.option.connection size', voltage: 'filter.p.m.product.voltage', pressure: 'filter.v.option.pressure', body: 'filter.p.m.product.body_material', seal: 'filter.p.m.product.seal_type', type: 'filter.p.product_type' };
  const INCH_TO_DN = {
    '1/4': 8, '3/8': 10, '1/2': 15, '3/4': 20, '1': 25, '1-1/4': 32, '1.25': 32, '1-1/2': 40, '1.5': 40,
    '2': 50, '2-1/2': 65, '2.5': 65, '3': 80, '4': 100, '5': 125, '6': 150, '8': 200, '10': 250, '12': 300,
    '14': 350, '16': 400, '20': 500, '24': 600,
  };
  const DN_TO_INCH = Object.fromEntries(Object.entries(INCH_TO_DN).filter(([k]) => !k.includes('.')).map(([k, v]) => [v, k.replace('1-1/4', '1¼').replace('1-1/2', '1½').replace('2-1/2', '2½').replace('1/2', '½').replace('3/4', '¾').replace('1/4', '¼').replace('3/8', '⅜')]));
  const VOLTAGE = { 12: ['12V'], 24: ['24V'], 110: ['110/220V'], 115: ['110/220V'], 120: ['110/220V'], 220: ['220V', '110/220V'], 230: ['220V', '110/220V'], 240: ['220V', '110/220V'], 250: ['250V'], 380: ['380V'], 400: ['380V'] };
  // [query words, chip label, test on a filter value's label]
  const BODY = [
    [/\b(stainless|inox|ss ?30[4l]|ss ?316l?|ss|aisi ?3\d\d|304l?|316l?|cf8m?|1\.4301|1\.4401)\b/, 'Stainless steel', /stainless/i],
    [/\bbrass\b/, 'Brass', /brass/i],
    [/\b(cast iron|ductile iron|ductile|ggg ?\d*|gg ?25|iron)\b/, 'Cast iron', /cast iron/i],
    [/\b(carbon steel|wcb|a216)\b/, 'Carbon steel', /carbon/i],
    [/\b(alumini?um|aluminum alloy)\b/, 'Aluminium', /alumin/i],
    [/\b(u?pvc|polyvinyl)\b/, 'PVC', /polyvinyl/i],
    [/\b(plastic|nylon)\b/, 'Plastic', /plastic|nylon|polyvinyl/i],
  ];
  const SEAL = [
    [/\bepdm\b/, 'EPDM', /^epdm/i],
    [/\b(nbr|buna|nitrile)\b/, 'NBR', /^nbr/i],
    [/\b(viton|fkm|fpm|fluorocarbon|fluorine rubber)\b/, 'Viton (FKM)', /viton|fluorocarbon|fluorine/i],
    [/\b(ptfe|teflon|fluoroplastic|rptfe)\b/, 'PTFE', /ptfe|fluoroplastic/i],
    [/\bsilicone?\b/, 'Silicone', /silicone/i],
    [/\b(metal seat(ed)?|metal seal(ed)?)\b/, 'Metal seat', /^metal$/i],
  ];
  // drive word → title syntax for valves (a kit's title names its actuator: "… with pneumatic double-acting actuator")
  const DRIVES = [
    [/\b(pneumatic|air[- ]actuated|air[- ]operated)\b/, 'pneumatic', 'Pneumatic', 'title:pneumatic'],
    [/\b(electric(al)?|motori[sz]ed|motor)\b/, 'electric', 'Electric', 'title:electric'],
    [/\b(manual|lever|handle|hand ?wheel)\b/, 'manual', 'Manual', '(title:lever OR title:handwheel OR title:handle)'],
  ];
  const STOP = new Set(['valve', 'valves', 'for', 'with', 'and', 'the', 'a', 'of', 'in', 'body', 'seal', 'seals', 'material', 'size', 'type', 'dnova', 'v', 'mm', 'inch', 'price', 'buy', 'industrial', 'operated', 'actuated']);
  const MODEL = /\b[a-z]{1,5}\d{2,}[a-z0-9()-]*\b|\b[a-z]\d{3}-\d{3,}\b/i;
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const typeName = (t) => {
    const s = t.replace(/^Water /, '');
    const n = s[0].toUpperCase() + s.slice(1).toLowerCase();
    return /s$|heating|equipment/.test(n) ? n : `${n}s`;
  };

  // ---------- query → plan ----------
  // plan = { chips: [{ key, kind, name, value, params: [[param, value]…], q? }], text, notes[], code }
  function plan(raw, v) {
    let q = ` ${raw.toLowerCase().replace(/[″”“]|''/g, '"').replace(/(\d) (\d\/\d)/g, '$1-$2')
      .replace(/(\d) ?(inch(es)?|in)\b/g, '$1"').replace(/\s+/g, ' ')} `;
    const chips = []; const notes = []; const back = [];
    const take = (re, fn) => { q = q.replace(re, (...m) => { fn(m); return ' '; }); };
    const vals = (param, test) => {
      const slot = v.values[param]; if (!slot) return [];
      return Object.entries(slot.values).filter(([label]) => test(label)).flatMap(([, list]) => list);
    };
    const add = (key, name, value, param, values, note) => {
      if (!values.length) { notes.push(`${name} ${value} is not a filter value in this store — searched as text.`); back.push(value.split(' · ')[0].toLowerCase()); return false; }
      chips.push({ key, kind: 'filter', name, value, params: values.map((x) => [param, x]) });
      if (note) notes.push(note);
      return true;
    };

    const sizes = [];
    take(/\bdn ?(\d{1,3})\b/g, (m) => sizes.push(+m[1]));
    take(/(?:^|\s)(\d{1,2}-\d\/\d|\d\/\d|\d{1,2}(?:\.\d+)?) ?"/g, (m) => { const dn = INCH_TO_DN[m[1]]; if (dn) sizes.push(dn); });
    for (const dn of [...new Set(sizes)]) add(`size:${dn}`, 'Size', `DN${dn}${DN_TO_INCH[dn] ? ` · ${DN_TO_INCH[dn]}″` : ''}`, P.size, vals(P.size, (l) => l === `DN${dn}`));

    let volt = null;
    take(/\b(\d{2,3})(?:\/\d{3})? ?v(ac|dc)?\b|\b(\d{2,3}) ?(vac|vdc|volts?)\b/g, (m) => {
      const n = +(m[1] || m[3]); const kind = ((m[2] || m[4] || '').match(/ac|dc/) || [''])[0].toUpperCase();
      if (VOLTAGE[n]) volt = { n, kind };
    });
    take(/\b(ac|dc)\b/g, (m) => { if (volt && !volt.kind) volt.kind = m[1].toUpperCase(); });
    if (volt) add(`voltage:${volt.n}`, 'Voltage', `${volt.n} V`, P.voltage, vals(P.voltage, (l) => VOLTAGE[volt.n].includes(l)),
      volt.kind ? `The voltage filter does not separate AC from DC: showing every ${volt.n} V coil — check ${volt.kind} on the product page.` : '');

    take(/\bpn ?(\d{1,3})\b|\b(\d{1,3}) ?bar\b/g, (m) => { const n = +(m[1] || m[2]); add(`pressure:${n}`, 'Pressure', `PN${n}`, P.pressure, vals(P.pressure, (l) => l === `${n} bar`)); });
    for (const [re, label, test] of SEAL) if (re.test(q)) { take(new RegExp(re.source, 'g'), () => {}); add(`seal:${label}`, 'Seal', label, P.seal, vals(P.seal, (l) => test.test(l))); break; }
    for (const [re, label, test] of BODY) if (re.test(q)) { take(new RegExp(re.source, 'g'), () => {}); add(`body:${label}`, 'Body', label, P.body, vals(P.body, (l) => test.test(l))); break; }

    let drive = null;
    for (const [re, key, label, syntax] of DRIVES) if (re.test(q)) { take(new RegExp(re.source, 'g'), () => {}); drive = { key, label, syntax }; break; }

    // family: the longest naming phrase wins ("ball check valve" over "ball", "knife gate" over "gate")
    let best = null;
    for (const f of v.families) for (const w of f.words) {
      const re = new RegExp(`\\b${w.replace(/[-]/g, '[- ]?').replace(/ /g, '\\s*')}s?\\b`);
      if (re.test(q) && (!best || w.length > best.w.length)) best = { f, w, re };
    }
    if (best) {
      take(new RegExp(best.re.source, 'g'), () => {});
      const f = best.f;
      let types = f.types;
      if (drive && f.drives && f.drives[drive.key]) { types = f.drives[drive.key]; drive = null; }
      chips.unshift({ key: `family:${f.name}`, kind: 'family', name: 'Category', value: types === f.types ? f.name : `${typeName(types[0])}`, params: types.map((t) => [P.type, t]), q: f.title ? `title:${f.title}` : '' });
    }
    if (drive) chips.push({ key: `drive:${drive.key}`, kind: 'drive', name: 'Drive', value: drive.label, params: [], q: drive.syntax });

    const code = MODEL.test(q);
    const words = `${q} ${back.join(' ')}`.split(' ').map((w) => w.replace(/[^a-z0-9/.+()-]/g, '')).filter((w) => w && (code || !STOP.has(w)));
    return { chips, text: words.join(' '), notes, code };
  }

  // ---------- plan ↔ URL ----------
  const IGNORE = new Set(['q', 'type', 'options[prefix]', 'options[unavailable_products]', 'view', 'page', 'dn_q', 'dn_raw']);
  function urlFor(chips, text, raw, extra = {}) {
    const u = new URLSearchParams();
    const syntax = chips.map((c) => c.q).filter(Boolean);
    u.set('q', [text, ...syntax].filter(Boolean).join(' ') || '*');
    u.set('type', 'product');
    u.set('options[prefix]', 'last');
    for (const c of chips) for (const [p, val] of c.params) u.append(p, val);
    if (raw) u.set('dn_q', raw);
    for (const [k, val] of Object.entries(extra)) u.set(k, val);
    return `/search?${u}`;
  }
  // The chips a URL describes, labelled from the vocabulary (values grouped the way the parser groups them).
  function chipsFromUrl(params, v) {
    const chips = []; let text = (params.get('q') || '').trim();
    const grab = (re) => { const m = text.match(re); if (m) text = text.replace(m[0], ' ').replace(/\s+/g, ' ').trim(); return m; };
    const typeVals = params.getAll(P.type);
    // family: a family whose types (and title word) are all present becomes one chip
    const fams = [...v.families].sort((a, b) => (b.title ? 1 : 0) - (a.title ? 1 : 0) || b.types.length - a.types.length);
    let left = [...typeVals];
    for (const f of fams) {
      const sets = [f.types, ...Object.values(f.drives || {})];
      for (const types of sets) {
        if (!types.every((t) => left.includes(t))) continue;
        if (f.title && !new RegExp(`(^|\\s)title:${f.title}(\\s|$)`).test(text)) continue;
        if (f.title) grab(new RegExp(`(^|\\s)title:${f.title}(?=\\s|$)`));
        left = left.filter((t) => !types.includes(t));
        chips.push({ key: `family:${f.name}`, kind: 'family', name: 'Category', value: types === f.types ? f.name : typeName(types[0]), params: types.map((t) => [P.type, t]), q: f.title ? `title:${f.title}` : '' });
      }
    }
    for (const t of left) chips.push({ key: `type:${t}`, kind: 'family', name: 'Category', value: typeName(t), params: [[P.type, t]] });
    for (const [re, key, label, syntax] of DRIVES) {
      const s = syntax.replace(/[()]/g, '\\$&');
      if (grab(new RegExp(`(^|\\s)${s}(?=\\s|$)`))) chips.push({ key: `drive:${key}`, kind: 'drive', name: 'Drive', value: label, params: [], q: syntax });
    }
    const groups = new Map();
    for (const [p, val] of params.entries()) {
      if (IGNORE.has(p) || p === P.type || p === 'sort_by' || !p.startsWith('filter.') || val === '') continue;
      // Trade's form always sends the price range, empty or not; a set price is one chip
      if (p === 'filter.v.price.gte' || p === 'filter.v.price.lte') {
        const key = 'price'; const g = groups.get(key) || { key, kind: 'filter', name: 'Price', value: '', params: [] };
        g.params.push([p, val]);
        const lo = g.params.find(([k]) => k.endsWith('gte'))?.[1]; const hi = g.params.find(([k]) => k.endsWith('lte'))?.[1];
        g.value = lo && hi ? `${lo}–${hi}` : lo ? `from ${lo}` : `up to ${hi}`;
        groups.set(key, g);
        continue;
      }
      const slot = v.values[p];
      const label = slot && Object.entries(slot.values).find(([, list]) => list.includes(val))?.[0];
      let name = slot ? slot.label : p.replace(/^filter\.[a-z]\.(m\.[a-z]+\.|option\.)?/, '');
      let value = label || val;
      if (p === P.size) { const dn = +(value.match(/\d+/) || [0])[0]; name = 'Size'; value = `${value}${DN_TO_INCH[dn] ? ` · ${DN_TO_INCH[dn]}″` : ''}`; }
      if (p === P.voltage) { name = 'Voltage'; value = value.replace(/V$/, ' V'); }
      if (p === P.pressure) { name = 'Pressure'; value = value.replace(/^(\d+) bar$/, 'PN$1'); }
      if (p === P.body) { name = 'Body'; value = (BODY.find(([, , t]) => t.test(value)) || [0, value])[1]; }
      if (p === P.seal) { name = 'Seal'; value = (SEAL.find(([, , t]) => t.test(value)) || [0, value])[1]; }
      const key = `${p}:${value}`;
      if (!groups.has(key)) groups.set(key, { key, kind: 'filter', name, value, params: [] });
      groups.get(key).params.push([p, val]);
    }
    // "220 V" is parsed to both 220V and 110/220V coils: one chip again
    const v220 = groups.get(`${P.voltage}:220 V`); const v110 = groups.get(`${P.voltage}:110/220 V`);
    if (v220 && v110) { v220.params.push(...v110.params); groups.delete(`${P.voltage}:110/220 V`); }
    chips.push(...groups.values());
    if (text === '*') text = '';
    return { chips, text };
  }
  const isPlain = (params) => ![...params.keys()].some((k) => k.startsWith('filter.')) && !/title:/.test(params.get('q') || '');

  // ---------- counts ----------
  const cache = new Map();
  const facets = (url) => {
    const u = `${url}${url.includes('?') ? '&' : '?'}view=dnfacets`;
    if (!cache.has(u)) cache.set(u, fetch(u).then((r) => r.json()).catch(() => null));
    return cache.get(u);
  };
  const results = (n, capped) => `${capped ? '1000+' : n.toLocaleString('en-US')} result${n === 1 ? '' : 's'}`;

  // ---------- the chip field (search page) ----------
  function chipHtml(c, i) {
    return `<span class="dn-chip" data-i="${i}"><span class="dn-chip__name">${esc(c.name)}</span> ${esc(c.value)}`
      + `<button type="button" class="dn-chip__x" data-dn-remove="${i}" aria-label="Remove ${esc(c.name)} ${esc(c.value)}">`
      + '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="m3 3 6 6m0-6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button></span>';
  }
  async function field() {
    const form = document.querySelector('.template-search__search form');
    const input = form && form.querySelector('input[name="q"]');
    if (!input) return;
    const v = await vocab(); if (!v) return;
    const params = new URLSearchParams(location.search);
    // a raw query that still holds specs (the header modal, an old link): convert once, keep "as text" one click away
    if (params.get('q') && isPlain(params) && !params.get('dn_raw')) {
      const p = plan(params.get('q'), v);
      if (p.chips.length) { location.replace(urlFor(p.chips, p.text, params.get('q'), params.get('sort_by') ? { sort_by: params.get('sort_by') } : {})); return; }
    }
    const fromUrl = chipsFromUrl(params, v);
    const state = { chips: fromUrl.chips, raw: params.get('dn_q') || '' };
    input.value = fromUrl.text;
    const box = input.closest('.field');
    box.classList.add('dn-sq');
    const list = document.createElement('span');
    list.className = 'dn-sq__chips';
    box.insertBefore(list, input);
    const read = document.querySelector('[data-dn-read]');
    let ticket = 0;
    const ORDER = { family: 0, drive: 1, filter: 2 };
    const applied = urlFor(fromUrl.chips, fromUrl.text, '');
    const render = () => {
      state.chips.sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);
      list.innerHTML = state.chips.map(chipHtml).join('');
      pending();
      box.classList.toggle('dn-sq--chips', state.chips.length > 0);
      input.placeholder = state.chips.length ? 'Add a spec or a word…' : input.getAttribute('data-dn-placeholder') || input.placeholder;
    };
    input.setAttribute('data-dn-placeholder', input.placeholder);
    render();
    const go = (chips, text) => { location.href = urlFor(chips, text, state.raw || '', params.get('sort_by') ? { sort_by: params.get('sort_by') } : {}); };
    // a recognised spec becomes a chip once its word is complete (space)
    const absorb = (all) => {
      const t = all ? input.value : input.value.replace(/\S+$/, '');
      if (!t.trim()) return false;
      const p = plan(t, v);
      const fresh = p.chips.filter((c) => !state.chips.some((s) => s.key === c.key));
      if (!fresh.length) return false;
      // a newer family replaces the old one ("ball" then "check" → Ball check valves)
      if (fresh.some((c) => c.kind === 'family')) state.chips = state.chips.filter((c) => c.kind !== 'family');
      state.chips.push(...fresh);
      state.raw = `${state.raw} ${t}`.trim();
      input.value = `${p.text}${all ? '' : input.value.slice(t.length)}`.replace(/^\s+/, '');
      render();
      return true;
    };
    // chips changed but not searched yet: the read line says what Enter will give, with the real count
    function pending() {
      if (!read) return;
      const url = urlFor(state.chips, input.value.trim(), '');
      if (url === applied) { if (read.dataset.dnApplied) { read.innerHTML = read.dataset.dnApplied; } return; }
      if (!read.dataset.dnApplied) read.dataset.dnApplied = read.innerHTML;
      const mine = ++ticket;
      read.hidden = false;
      read.innerHTML = 'Counting…';
      facets(url).then((d) => {
        if (mine !== ticket || !d) return;
        read.innerHTML = `Press Enter: <strong>${d.count ? results(d.count, d.capped) : 'no results'}</strong> with these filters.`;
      });
    }
    input.addEventListener('input', (e) => { if (e.data === ' ' || e.inputType === 'insertFromPaste') absorb(e.inputType === 'insertFromPaste'); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !input.value && state.chips.length && input.selectionStart === 0) {
        e.preventDefault();
        state.chips.pop();
        render();
      }
    });
    list.addEventListener('click', (e) => {
      const b = e.target.closest('[data-dn-remove]'); if (!b) return;
      state.chips.splice(+b.dataset.dnRemove, 1);
      go(state.chips, input.value.trim());
    });
    form.addEventListener('submit', (e) => {
      if (form.querySelector('[aria-selected="true"] a')) return;
      e.preventDefault(); e.stopImmediatePropagation();
      absorb(true);
      go(state.chips, input.value.trim());
    }, true);
    readLine(state, fromUrl.text, v);
    // a filter ticked in the sidebar re-renders the grid and pushes a new URL without a reload
    let last = location.search;
    new MutationObserver(() => {
      if (location.search === last) return;
      last = location.search;
      const now = chipsFromUrl(new URLSearchParams(location.search), v);
      state.chips = now.chips;
      render();
    }).observe(document.getElementById('ProductGridContainer') || document.body, { childList: true, subtree: true });
  }

  // "Read as": what the search did, what it could not, and the way back to plain text
  async function readLine(state, text, v) {
    const el = document.querySelector('[data-dn-read]');
    if (!el) return;
    const raw = state.raw;
    const p = raw ? plan(raw, v) : { notes: [] };
    const parts = [];
    if (state.chips.length) parts.push(`${state.chips.length} filter${state.chips.length === 1 ? '' : 's'}`);
    if (text) parts.push(`text “${esc(text)}”`);
    if (!parts.length) return;
    const notes = p.notes.length ? ` <span class="dn-read__note">${p.notes.map(esc).join(' ')}</span>` : '';
    const asText = raw ? ` · <a href="/search?q=${encodeURIComponent(raw)}&type=product&options%5Bprefix%5D=last&dn_raw=1">Search “${esc(raw)}” as text instead</a>` : '';
    el.innerHTML = `Searching ${parts.join(' + ')}.${notes}${asText}`;
    el.hidden = !state.chips.length && !raw;
  }

  // ---------- the sidebar: Categories first, readable names ----------
  function sidebar() {
    // facets.js replaces the whole filter column after every AJAX filter, so watch the section, not the aside
    const root = document.querySelector('.template-search');
    if (!root) return;
    const tidy = () => {
      root.querySelectorAll('details.facets__disclosure-vertical, details[id^="Details-"]').forEach((d) => {
        const typeInput = d.querySelector(`input[name="${P.type}"]`);
        if (!typeInput || d.dataset.dnCat) return;
        d.dataset.dnCat = '1';
        d.classList.add('dn-cat');
        d.open = true;
        d.parentElement.prepend(d);
        const label = d.querySelector('.facets__summary-label');
        if (label && label.firstChild) label.firstChild.textContent = 'Categories';
        d.querySelectorAll('.facet-checkbox__text-label').forEach((s) => { s.textContent = typeName(s.textContent.trim()); });
      });
    };
    tidy();
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; tidy(); });
    }).observe(root, { childList: true, subtree: true });
  }

  // Shopify stops counting at 1000 and prints "1000 results" as if it were a count; rewritten here rather
  // than forking Trade's facets snippet (shared with collections), again after every AJAX render.
  function capCount() {
    document.querySelectorAll('#ProductCount, #ProductCountDesktop, .product-count__text').forEach((el) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (/^\s*1,?000 results?/.test(n.nodeValue)) n.nodeValue = n.nodeValue.replace(/1,?000 results?/, '1000+ results');
      }
    });
  }
  function watchCount() {
    capCount();
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; capCount(); });
    }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
  }

  // No results: say which chip to drop, with the count it would give
  async function relax() {
    const el = document.querySelector('[data-dn-relax]');
    if (!el) return;
    const v = await vocab(); if (!v) return;
    const params = new URLSearchParams(location.search);
    const { chips, text } = chipsFromUrl(params, v);
    if (!chips.length && !text) return;
    const options = [...chips.map((c, i) => ({ label: `without ${c.name} ${c.value}`, url: urlFor(chips.filter((_, j) => j !== i), text, params.get('dn_q')) }))];
    if (text && chips.length) options.push({ label: `without the text “${text}”`, url: urlFor(chips, '', params.get('dn_q')) });
    const counted = (await Promise.all(options.map(async (o) => ({ ...o, data: await facets(o.url) })))).filter((o) => o.data && o.data.count > 0)
      .sort((a, b) => a.data.count - b.data.count).slice(0, 4);
    if (!counted.length) return;
    el.innerHTML = `<p class="dn-relax__head">Closest matches</p>${counted.map((o) => `<a class="dn-relax__row" href="${esc(o.url)}"><span>${esc(o.label)}</span><span class="dn-relax__count">${results(o.data.count, o.data.capped)} →</span></a>`).join('')}`;
    el.hidden = false;
  }

  // ---------- every other search field: same URL on submit ----------
  function hookForms() {
    document.querySelectorAll('form[action$="/search"]').forEach((form) => {
      if (form.dataset.dnHooked || form.closest('.template-search__search')) return;
      form.dataset.dnHooked = '1';
      form.addEventListener('submit', async (e) => {
        const input = form.querySelector('input[name="q"]');
        const raw = input && input.value.trim();
        if (!raw || form.querySelector('[aria-selected="true"] a')) return;
        e.preventDefault(); e.stopImmediatePropagation();
        const v = await vocab();
        if (!v) { location.href = `/search?q=${encodeURIComponent(raw)}&type=product`; return; }
        const p = plan(raw, v);
        location.href = p.chips.length ? urlFor(p.chips, p.text, raw) : `/search?q=${encodeURIComponent(raw)}&type=product&options%5Bprefix%5D=last`;
      }, true);
    });
  }

  // ---------- the dropdown (Trade's predictive panel) ----------
  async function panelFor(ps, term) {
    const v = await vocab(); if (!v) return null;
    const onSearchPage = !!ps.closest('.template-search__search');
    let chips; let text; let raw = term;
    if (onSearchPage) {
      const params = new URLSearchParams(location.search);
      const base = chipsFromUrl(params, v);
      const p = plan(term, v);
      chips = [...base.chips.filter((c) => !(p.chips.some((n) => n.kind === 'family') && c.kind === 'family')), ...p.chips.filter((c) => !base.chips.some((b) => b.key === c.key))];
      text = p.text; raw = `${params.get('dn_q') || ''} ${term}`.trim();
      ps.toggleAttribute('data-dn-code', p.code);
    } else {
      const p = plan(term, v);
      chips = p.chips; text = p.text;
      ps.toggleAttribute('data-dn-code', p.code);
    }
    if (!chips.length) return null;
    const url = urlFor(chips, text, raw);
    const noType = chips.filter((c) => c.kind !== 'family');
    const [main, wide] = await Promise.all([facets(url), facets(urlFor(noType, text, raw))]);
    if (!main) return null;
    const cat = wide && !wide.capped && wide.filters.find((f) => f.param === P.type);
    const cats = cat ? cat.values.filter((x) => x.count > 0).sort((a, b) => b.count - a.count).slice(0, 4) : [];
    return { chips, url, main, cats, catUrl: (t) => urlFor([...noType, { params: [[P.type, t]] }], text, raw) };
  }
  function panelHtml(r) {
    const chips = r.chips.map((c) => `<span class="dn-spec__chip"><span class="dn-chip__name">${esc(c.name)}</span> ${esc(c.value)}</span>`).join('');
    const cats = r.cats.map((c, i) => `
      <li id="dn-spec-cat-${i}" class="predictive-search__list-item dn-spec__item" role="option" aria-selected="false">
        <a class="predictive-search__item dn-spec__row dn-spec__row--cat link" href="${esc(r.catUrl(c.value))}" tabindex="-1">
          <span class="dn-spec__fam">${esc(typeName(c.label))}</span><span class="dn-spec__count">${c.count}</span>
        </a>
      </li>`).join('');
    return `<div class="dn-spec" data-dn-spec>
      <ul class="list-unstyled dn-spec__list" role="group" aria-label="Search with filters">
        <li id="dn-spec-main" class="predictive-search__list-item dn-spec__item" role="option" aria-selected="false">
          <a class="predictive-search__item dn-spec__row link" href="${esc(r.url)}" tabindex="-1">
            <span class="dn-spec__chips">${chips}</span>
            <span class="dn-spec__count">${r.main.count ? results(r.main.count, r.main.capped) : 'no results'}</span>
          </a>
        </li>
      </ul>
      ${cats ? `<h2 class="predictive-search__heading dn-spec__head">In categories</h2><ul class="list-unstyled dn-spec__list" role="group" aria-label="Categories">${cats}</ul>` : ''}
    </div>`;
  }
  function paint(ps) {
    const panel = ps.querySelector('[data-predictive-search]');
    const list = panel && panel.querySelector('#predictive-search-results');
    const input = ps.querySelector('input[type="search"]');
    if (!list || !input) return;
    const term = input.value.trim();
    const old = list.querySelector('[data-dn-spec]');
    if (old && old.dataset.term !== term) old.remove();
    panelFor(ps, term).then((r) => {
      if (input.value.trim() !== term) return;
      const cur = list.querySelector('[data-dn-spec]');
      if (!r) { cur && cur.remove(); return; }
      if (cur && cur.dataset.term === term) return;
      cur && cur.remove();
      list.insertAdjacentHTML('afterbegin', panelHtml(r));
      list.querySelector('[data-dn-spec]').dataset.term = term;
    }).catch(() => {});
  }
  function hookPanels() {
    document.querySelectorAll('predictive-search').forEach((ps) => {
      if (ps.dataset.dnHooked) return;
      ps.dataset.dnHooked = '1';
      const panel = ps.querySelector('[data-predictive-search]');
      const input = ps.querySelector('input[type="search"]');
      if (!panel || !input) return;
      new MutationObserver(() => paint(ps)).observe(panel, { childList: true });
      input.addEventListener('input', () => { vocab().then((v) => v && ps.toggleAttribute('data-dn-code', plan(input.value, v).code)); });
    });
  }

  const start = () => {
    hookPanels(); hookForms();
    if (document.querySelector('.template-search')) { field(); sidebar(); watchCount(); relax(); }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  window.DnovaSpec = { plan: async (q) => plan(q, await vocab()), url: async (q) => { const p = plan(q, await vocab()); return urlFor(p.chips, p.text, q); } };
})();
